import { Controller } from "@hotwired/stimulus";

// Handles PDF preview modal with accessibility features
// RGAA 4.1 / WCAG 2.2 compliant implementation
export default class extends Controller {
  static targets = ["iframe", "loader", "container", "error"];
  static values = {
    url: String,
    loadingTimeout: { type: Number, default: 10000 },
  };

  connect() {
    this.loadTimeoutId = null;
    this.checkIntervalId = null;
    this.isLoading = false;
    this.hasLoaded = false;

    // Observer pour détecter quand la modal devient visible
    this.observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.type === "attributes" &&
          mutation.attributeName === "class"
        ) {
          const isVisible = !this.element.classList.contains("hidden");
          if (isVisible && !this.hasLoaded && !this.isLoading) {
            this.loadPDF();
          }
        }
      });
    });

    // Observer les changements de classe pour détecter l'ouverture
    this.observer.observe(this.element, {
      attributes: true,
      attributeFilter: ["class"],
    });

    // Si la modal est déjà visible au moment du connect, charger immédiatement
    if (!this.element.classList.contains("hidden")) {
      this.loadPDF();
    }
  }

  disconnect() {
    this.clearLoadTimeout();
    this.clearCheckInterval();
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  // Recharge la preview avec une nouvelle URL (modal partagé dont la page
  // #page=N change d'une ouverture à l'autre). Appelé de façon déterministe par
  // la passerelle qualiopi-indicator-fiche, AVANT l'ouverture ; les
  // consommateurs à URL fixe (certificats, conventions…) ne l'utilisent pas.
  reloadWith(url) {
    this.urlValue = url;
    this.hasLoaded = false;
    this.isLoading = false;
    // Repart d'une iframe vide pour garantir un VRAI (re)chargement au prochain
    // loadPDF, même si seule l'ancre #page change ou si on rouvre le même
    // indicateur (un changement de fragment seul — ou une src identique — ne
    // rechargerait pas). L'URL restant identique (même chemin), le PDF est servi
    // depuis le cache navigateur : pas de re-téléchargement.
    if (this.hasIframeTarget) {
      this.iframeTarget.src = "about:blank";
    }
    if (!this.element.classList.contains("hidden")) {
      this.loadPDF();
    }
  }

  loadPDF() {
    if (!this.hasIframeTarget || !this.urlValue) {
      this.showError();
      return;
    }

    if (this.isLoading) {
      return;
    }

    this.isLoading = true;
    this.hasLoaded = false;
    this.showLoader();
    this.hideContainer();
    this.hideError();

    this.loadTimeoutId = setTimeout(() => {
      this.handleLoadTimeout();
    }, this.loadingTimeoutValue);

    // Charger directement le PDF dans l'iframe
    const iframeElement = this.iframeTarget;
    iframeElement.src = this.urlValue;

    // Démarrer la vérification du chargement
    this.startLoadCheck();
  }

  startLoadCheck() {
    // Vérifier périodiquement si le PDF est chargé dans l'iframe
    let checkCount = 0;
    const maxChecks = 100; // 10 secondes max (100 * 100ms)

    const iframeElement = this.iframeTarget;

    const handleLoad = () => {
      this.clearCheckInterval();
      this.handleLoadSuccess();
    };

    const handleError = () => {
      this.clearCheckInterval();
      this.handleLoadError();
    };

    // Ajouter les listeners d'événements sur l'iframe
    iframeElement.addEventListener("load", handleLoad, { once: true });
    iframeElement.addEventListener("error", handleError, { once: true });

    // Vérification périodique en fallback (pour les cas où les événements ne se déclenchent pas)
    this.checkIntervalId = setInterval(() => {
      checkCount++;

      try {
        // Vérifier si l'iframe a un document avec readyState
        if (iframeElement.contentDocument || iframeElement.contentWindow) {
          const doc =
            iframeElement.contentDocument ||
            iframeElement.contentWindow?.document;
          if (doc && doc.readyState === "complete") {
            // Le document est chargé
            this.clearCheckInterval();
            iframeElement.removeEventListener("load", handleLoad);
            iframeElement.removeEventListener("error", handleError);
            this.handleLoadSuccess();
            return;
          }
        }

        // Après 1 seconde, si l'iframe a une hauteur significative, considérer comme chargé
        // (même si on ne peut pas accéder au document à cause de CORS)
        if (checkCount >= 10 && iframeElement.offsetHeight > 100) {
          this.clearCheckInterval();
          iframeElement.removeEventListener("load", handleLoad);
          iframeElement.removeEventListener("error", handleError);
          this.handleLoadSuccess();
          return;
        }
      } catch (e) {
        // Erreur CORS - c'est normal pour les PDFs dans certains navigateurs
        // Si on arrive ici après 1 seconde, considérer comme chargé
        // (l'iframe essaie de charger le PDF même si on ne peut pas y accéder)
        if (checkCount >= 10) {
          this.clearCheckInterval();
          iframeElement.removeEventListener("load", handleLoad);
          iframeElement.removeEventListener("error", handleError);
          this.handleLoadSuccess();
          return;
        }
      }

      if (checkCount >= maxChecks) {
        this.clearCheckInterval();
        iframeElement.removeEventListener("load", handleLoad);
        iframeElement.removeEventListener("error", handleError);
        this.handleLoadTimeout();
      }
    }, 100);
  }

  handleLoadSuccess() {
    this.clearLoadTimeout();
    this.clearCheckInterval();
    this.isLoading = false;
    this.hasLoaded = true;
    this.hideLoader();
    this.showContainer();
    this.hideError();

    this.announceToScreenReader(
      this.getTranslation("success", "PDF chargé avec succès")
    );
  }

  handleLoadError() {
    this.clearLoadTimeout();
    this.clearCheckInterval();
    this.isLoading = false;
    this.hasLoaded = false;
    this.hideLoader();
    this.hideContainer();
    this.showError();

    this.announceToScreenReader(
      this.getTranslation("error", "Erreur de chargement du PDF")
    );
  }

  handleLoadTimeout() {
    this.clearLoadTimeout();
    this.clearCheckInterval();
    this.isLoading = false;
    this.hasLoaded = false;
    this.hideLoader();
    this.hideContainer();
    this.showError();

    this.announceToScreenReader(
      this.getTranslation("timeout", "Le chargement du PDF a expiré")
    );
  }

  close(event) {
    event?.preventDefault();
    // Déléguer la fermeture au contrôleur modal global
    const modalElement = this.element;
    if (modalElement) {
      // Réinitialiser l'état pour permettre un nouveau chargement
      this.resetState();

      // Trouver le contrôleur modal sur le body
      const modalController =
        this.application.getControllerForElementAndIdentifier(
          document.body,
          "modal"
        );
      if (modalController && typeof modalController.closeModal === "function") {
        modalController.closeModal(modalElement);
      } else {
        // Fallback: fermer manuellement
        modalElement.classList.add("hidden");
        modalElement.setAttribute("aria-hidden", "true");
        document.body.style.overflow = "";
      }
    }
  }

  resetState() {
    // Réinitialiser l'état pour permettre un nouveau chargement
    this.clearLoadTimeout();
    this.clearCheckInterval();
    this.isLoading = false;
    this.hasLoaded = false;

    // Réinitialiser l'affichage
    this.showLoader();
    this.hideContainer();
    this.hideError();

    // Réinitialiser l'iframe
    if (this.hasIframeTarget) {
      this.iframeTarget.src = "";
    }
  }

  showLoader() {
    if (this.hasLoaderTarget) {
      this.loaderTarget.classList.remove("hidden");
    }
  }

  hideLoader() {
    if (this.hasLoaderTarget) {
      this.loaderTarget.classList.add("hidden");
    }
  }

  showContainer() {
    if (this.hasContainerTarget) {
      this.containerTarget.classList.remove("hidden");
    }
  }

  hideContainer() {
    if (this.hasContainerTarget) {
      this.containerTarget.classList.add("hidden");
    }
  }

  showError() {
    if (this.hasErrorTarget) {
      this.errorTarget.classList.remove("hidden");
    }
  }

  hideError() {
    if (this.hasErrorTarget) {
      this.errorTarget.classList.add("hidden");
    }
  }

  clearLoadTimeout() {
    if (this.loadTimeoutId) {
      clearTimeout(this.loadTimeoutId);
      this.loadTimeoutId = null;
    }
  }

  clearCheckInterval() {
    if (this.checkIntervalId) {
      clearInterval(this.checkIntervalId);
      this.checkIntervalId = null;
    }
  }

  announceToScreenReader(message) {
    const announcement = document.createElement("div");
    announcement.setAttribute("role", "status");
    announcement.setAttribute("aria-live", "polite");
    announcement.setAttribute("aria-atomic", "true");
    announcement.className = "sr-only";
    announcement.textContent = message;

    document.body.appendChild(announcement);

    setTimeout(() => {
      if (announcement.parentNode) {
        document.body.removeChild(announcement);
      }
    }, 2000);
  }

  getTranslation(key, fallback) {
    // Try to get translation from data attributes or use fallback
    const translations = {
      success: "PDF chargé avec succès",
      error: "Erreur de chargement du PDF",
      timeout: "Le chargement du PDF a expiré",
    };
    return translations[key] || fallback;
  }
}
